import React from 'react';
import PropTypes from 'prop-types';

import { COLOR, SIZE } from '@components/SimpleButton';
import Modal from '@components/ResponsiveModal';
import useIsDesktopLayout from '@hooks/useIsDesktopLayout';
import theme from '@app/theme';
import LoadingIcon from '@components/LoadingIcon';
import Container from './components/Container';
import Headline from './components/Headline';
import Text from './components/Text';
import Buttons from './components/Buttons';
import ButtonContainer from './components/ButtonContainer';
import Loading from './components/Loading';

import { BUTTONS_TYPE } from './constants';

const showButton = (text, onClick) => !!(text && onClick);

function Dialog({
  open,
  title,
  text,
  image,
  buttonsType,
  onFirstOptionClick,
  onSecondOptionClick,
  onClose,
  firstButtonText,
  secondButtonText,
  persuasiveButtons,
  children,
  loading,
}) {
  const showFirstButton = showButton(firstButtonText, onFirstOptionClick);
  const showSecondButton = showButton(secondButtonText, onSecondOptionClick);
  const isDesktopLayout = useIsDesktopLayout();

  const customStyles = {
    modal: {
      display: 'flex',
      flex: 1,
      borderRadius: '6px',
      padding: '0px',
      margin: isDesktopLayout ? 'auto' : 'auto 24px',
      boxShadow: `0 32px 40px -10px ${theme.colors.grape_50}`,
      maxWidth: '540px',
    },
  };

  const firstButtonColor =
    buttonsType === BUTTONS_TYPE.STACKED ? COLOR.PRIMARY : COLOR.SECONDARY;

  const secondButtonColor =
    buttonsType === BUTTONS_TYPE.STACKED ? COLOR.SECONDARY : COLOR.PRIMARY;

  return (
    <Modal
      open={open}
      center
      showCloseIcon={false}
      onClose={onClose}
      styles={customStyles}
    >
      <Container>
        {image}
        <Headline>{title}</Headline>
        <Text>{text}</Text>
        {children}
        <Buttons buttonsType={buttonsType}>
          {showFirstButton && (
            <ButtonContainer
              buttonsType={buttonsType}
              elementId="dialog__firstOption"
              label={firstButtonText}
              onClick={onFirstOptionClick}
              color={persuasiveButtons ? COLOR.SECONDARY : firstButtonColor}
              size={SIZE.SMALL}
            />
          )}

          {showSecondButton && (
            <ButtonContainer
              buttonsType={buttonsType}
              elementId="dialog__secondOption"
              label={secondButtonText}
              onClick={onSecondOptionClick}
              color={persuasiveButtons ? COLOR.SECONDARY : secondButtonColor}
              size={SIZE.SMALL}
            />
          )}
        </Buttons>
        {loading && (
          <Loading>
            <LoadingIcon />
          </Loading>
        )}
      </Container>
    </Modal>
  );
}

Dialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  image: PropTypes.object,
  text: PropTypes.string,
  buttonsType: PropTypes.oneOf(Object.values(BUTTONS_TYPE)),
  onFirstOptionClick: PropTypes.func,
  onSecondOptionClick: PropTypes.func,
  onClose: PropTypes.func,
  firstButtonText: PropTypes.string,
  secondButtonText: PropTypes.string,
  persuasiveButtons: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node,
};

Dialog.defaultProps = {
  buttonsType: BUTTONS_TYPE.STACKED,
  persuasiveButtons: false,
  loading: false,
};

export default Dialog;
